/**
 * Centralized logging utility for HydroApp
 * Provides environment-aware logging with log levels and module-specific loggers
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  minLevel: LogLevel;
  enableInProduction: boolean;
  prefix?: string;
}

class Logger {
  private config: LoggerConfig;
  private readonly levels: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
  };

  constructor(config: LoggerConfig) {
    this.config = config;
  }

  private shouldLog(level: LogLevel): boolean {
    // Don't log in production unless explicitly enabled
    if (!this.config.enableInProduction && import.meta.env.PROD) {
      return false;
    }
    // Check if this level meets the minimum threshold
    return this.levels[level] >= this.levels[this.config.minLevel];
  }

  private formatMessage(level: LogLevel, message: string, data?: any): void {
    const prefix = this.config.prefix || 'App';
    const timestamp = new Date().toISOString();
    const emoji = {
      debug: '🔍',
      info: 'ℹ️',
      warn: '⚠️',
      error: '❌',
    }[level];

    const formattedMsg = `[${timestamp}] ${emoji} ${prefix}:${level.toUpperCase()} - ${message}`;

    if (data !== undefined) {
      console[level](formattedMsg, data);
    } else {
      console[level](formattedMsg);
    }
  }

  debug(message: string, data?: any): void {
    if (this.shouldLog('debug')) {
      this.formatMessage('debug', message, data);
    }
  }

  info(message: string, data?: any): void {
    if (this.shouldLog('info')) {
      this.formatMessage('info', message, data);
    }
  }

  warn(message: string, data?: any): void {
    if (this.shouldLog('warn')) {
      this.formatMessage('warn', message, data);
    }
  }

  error(message: string, data?: any): void {
    if (this.shouldLog('error')) {
      this.formatMessage('error', message, data);
    }
  }
}

/**
 * Create a logger instance for a specific module
 * @param prefix - Module name to use as prefix in logs
 * @returns Logger instance
 */
export const createLogger = (prefix: string): Logger => {
  return new Logger({
    minLevel: import.meta.env.DEV ? 'debug' : 'warn',
    enableInProduction: false,
    prefix,
  });
};

// Pre-configured loggers for common modules
export const notificationLogger = createLogger('Notification');
export const authLogger = createLogger('Auth');
export const storageLogger = createLogger('Storage');
export const hydrationLogger = createLogger('Hydration');
export const apiLogger = createLogger('API');
export const appLogger = createLogger('App');

export default Logger;

